/**
 * The selected run, as a card with a metric row.
 *
 * What a reader needs from a run before reading its ranking is: which run, whether
 * it is still going, how far it got, what it cost — and **which criteria and which
 * graph it scored against**, because `context/` is a mutable S3 mirror and the graph
 * is rebuilt, so two runs a day apart can disagree for reasons that have nothing to
 * do with the science. That last part is the provenance block at the bottom and it is
 * the only place in this UI where that difference is visible.
 *
 * ── Three rules this file exists to keep ────────────────────────────────────────
 *
 * **A counter that was never recorded says so in words.** `cache_hit_rate` is `null`
 * on every run written before infra/sql/002_run_usage.sql and on any run that has not
 * called a model yet; both are "not measured", and neither is 0%. The earlier version
 * hid the whole spend block when `llm_calls === 0` to avoid printing "$0.00" on a
 * sweep that cost real money — right instinct, wrong mechanism: hiding a metric makes
 * it indistinguishable from a metric that is zero, one row further down the page. The
 * metric row is now stable and the unmeasured cells say "Not measured".
 *
 * **Absent, `'unknown'` and a real value are three states per provenance key.**
 * Absent means this run's harness never recorded it. `'unknown'` means the harness
 * looked and could not tell — which is a finding, because it is what forbids the run
 * from stating a post-build literature boundary. They are rendered differently on
 * purpose; a dash for both would collapse them.
 *
 * **The stop button keeps every message it had.** `POST /cancel` distinguishes
 * `requested` (a live sweep will notice within ~10s) from `reaped` (its owner was
 * already gone and the row was tidied), and both are real readings a reader has to be
 * able to tell apart, so the API's own sentence is rendered verbatim rather than
 * summarised to "Stopped".
 */
import { STAGE_LABELS } from '../lib/stages'
import { formatDuration, formatTimestamp } from '../lib/utils'
import { Button } from './ui/Button'
import { Card, CardBody, CardHeader } from './ui/Card'
import { FailedState } from './ui/States'
import { StatusDot } from './ui/StatusDot'
import type { ReactNode } from 'react'
import type { RunDetail, RunSummary } from '../api/types'

/** Statuses a run can still be stopped from. Anything else has finished. */
const STOPPABLE = new Set(['queued', 'running'])

/** Run detail header: the metadata above the ranking. */
export function RunHeader({
  run,
  targetCount,
  onStop,
  stopping,
  stopNotice,
}: {
  run: RunSummary | RunDetail
  targetCount: number
  onStop: (runId: string) => void
  /** True while this run's stop request is in flight. */
  stopping: boolean
  stopNotice: string | null
}) {
  const stoppable = STOPPABLE.has(run.status)
  const requested = run.cancel_requested_at != null

  // Every counter is 0 for a run older than infra/sql/002_run_usage.sql AND for one
  // that has not reached a model yet. Two causes, one value — so the copy names
  // neither and says only what is true: nobody measured it.
  const metered = run.llm_calls > 0

  return (
    <Card className="animate-rise">
      <CardHeader
        eyebrow="Run"
        title={<span className="font-mono">{run.run_id}</span>}
        description={run.prompt ?? undefined}
        actions={
          <>
            {/* `StatusDot` rather than a second status map: it falls back to a
                neutral dot and the raw value, so an unknown status renders as
                itself instead of being rounded up to "Succeeded". */}
            <StatusDot status={run.status} showLabel />
            {stoppable && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => onStop(run.run_id)}
                disabled={stopping || requested}
                title={
                  requested
                    ? 'A stop has already been requested; the sweep aborts within about 10 seconds.'
                    : 'Stop this sweep. The checkpoint is kept, so the same thread can be resumed.'
                }
              >
                {stopping ? 'Stopping…' : requested ? 'Stop requested' : 'Stop run'}
              </Button>
            )}
          </>
        }
      />

      <CardBody className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-6">
          <Metric label="Targets ranked">
            <span className="font-display text-base font-semibold tabular-nums text-slate-100">
              {targetCount}
            </span>
          </Metric>
          <Metric label="Stage">
            <span className="text-slate-200">
              {/* Unknown stages fall through verbatim: `stage` is free text and the
                  harness grows steps faster than the label map. */}
              {run.stage ? (STAGE_LABELS[run.stage] ?? run.stage) : 'Not recorded'}
            </span>
          </Metric>
          <Metric label="Elapsed" mono>
            {formatDuration(run.started_at, run.finished_at)}
          </Metric>
          <Metric
            label="Model calls"
            mono
            unmeasured={!metered}
            hint={UNMETERED_HINT}
          >
            {run.llm_calls.toLocaleString()}
          </Metric>
          {/* Beside the cost because it is what the cost turns on: a cache read
              bills at about a tenth of a fresh input token, so a change that cuts
              tokens while dropping this rate can cost more. `null` is not 0%. */}
          <Metric
            label="Cache hits"
            mono
            unmeasured={run.cache_hit_rate == null}
            hint="No cache-hit rate was recorded for this run. It is a share of input tokens, so a run that never called a model has none — and neither has any run written before infra/sql/002_run_usage.sql. It is not 0%."
          >
            {run.cache_hit_rate != null
              ? `${Math.round(run.cache_hit_rate * 100)}%`
              : null}
          </Metric>
          <Metric
            label="Est. cost"
            mono
            unmeasured={!metered}
            hint={UNMETERED_HINT}
          >
            {`$${run.cost_usd.toFixed(2)}`}
          </Metric>
        </dl>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-slate-800 pt-4 sm:grid-cols-4">
          <Metric label="Started" mono>
            {formatTimestamp(run.started_at)}
          </Metric>
          <Metric label={run.finished_at ? 'Finished' : 'Last update'} mono>
            {formatTimestamp(run.finished_at ?? run.updated_at)}
          </Metric>
          <Metric
            label="Input tokens"
            mono
            unmeasured={!metered}
            hint={UNMETERED_HINT}
          >
            {formatTokens(run.input_tokens)}
          </Metric>
          <Metric
            label="Output tokens"
            mono
            unmeasured={!metered}
            hint={UNMETERED_HINT}
          >
            {formatTokens(run.output_tokens)}
          </Metric>
        </dl>

        <Provenance run={run} />

        {stopNotice && (
          // The API's own sentence, verbatim: `requested` and `reaped` are
          // different outcomes and only this string says which happened. Also
          // carries the client-side failure message when the call itself failed.
          <p className="rounded-lg border border-slate-700 bg-slate-800/40 px-3 py-2 text-xs text-slate-300">
            {stopNotice}
          </p>
        )}

        {run.last_error && (
          // `whitespace-pre-wrap` on the wrapper, not the text: `white-space` is
          // inherited, so this reaches `FailedState`'s paragraph without a copy of
          // the component. The store keeps 4000 characters and this is the only
          // place a stopped sweep explains itself, so nothing is truncated.
          <FailedState
            className="whitespace-pre-wrap"
            message={run.last_error}
          />
        )}
      </CardBody>
    </Card>
  )
}

/**
 * Why a counter can read "Not measured" while the run plainly did work.
 *
 * One string, because it hangs off four metrics and four copies would drift into
 * four different explanations of one fact.
 */
const UNMETERED_HINT =
  'This run recorded no token counters. Either it has not called a model yet, or it ' +
  'predates infra/sql/002_run_usage.sql — the counters were never backfilled, so a ' +
  'zero here would be an invented measurement.'

/**
 * One figure in the metric row.
 *
 * `unmeasured` is a separate prop rather than "render 'Not measured' when children
 * are falsy", because `0` and `$0.00` are legitimate measured values and a truthiness
 * test would silently relabel them as absent — the exact substitution this file is
 * here to prevent.
 */
function Metric({
  label,
  children,
  mono = false,
  unmeasured = false,
  hint,
}: {
  label: string
  children?: ReactNode
  mono?: boolean
  unmeasured?: boolean
  hint?: string
}) {
  return (
    <div className="min-w-0">
      <dt className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
        {label}
      </dt>
      <dd className="mt-1 truncate text-sm">
        {unmeasured ? (
          <span className="text-2xs text-slate-500" title={hint}>
            Not measured
          </span>
        ) : mono ? (
          <span className="font-mono tabular-nums text-slate-200">{children}</span>
        ) : (
          children
        )}
      </dd>
    </div>
  )
}

/**
 * Which criteria this run read and how old the graph was.
 *
 * Always rendered, and that is the change from the earlier version, which hid each
 * line when its key was absent. Hiding is what made "this harness never recorded it"
 * indistinguishable from "the field is fine, it is just not interesting" — and the
 * two lead to opposite conclusions about whether two runs are comparable. Three
 * states per key, worded differently:
 *
 * - a value — the hash or the date, with what it means
 * - `'unknown'` — the harness looked and could not tell. A finding, in amber
 * - absent or null — this run's harness never recorded it. Stated, not dashed
 */
function Provenance({ run }: { run: RunSummary | RunDetail }) {
  return (
    <dl className="space-y-2 border-t border-slate-800 pt-4 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <dt className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
          Criteria
        </dt>
        <dd className="min-w-0 text-slate-500">
          {!run.context_version ? (
            <span title="The columns arrive with infra/sql/007_context_version.sql. A run written before it recorded no hash, and nothing was backfilled — so which rubric it scored against cannot be established from this row.">
              Not recorded — this run predates the criteria stamp, so which rubric
              it scored against is not established
            </span>
          ) : run.context_version === 'unknown' ? (
            <>
              <span className="font-mono text-amber-400">unknown</span>
              <span className="ml-2">
                — the context tree could not be read, so this run cannot be
                compared to another by rubric
              </span>
            </>
          ) : (
            <>
              <span
                className="font-mono text-slate-300"
                title="Content hash of the context/ tree the agents read: the scoring rubric, program scope, red flags and glossary. Two runs sharing it scored against the same criteria."
              >
                {run.context_version}
              </span>
              <span className="ml-2">
                {run.context_source === 's3'
                  ? '— an override published to S3 was applied over the committed files'
                  : run.context_source === 'baked'
                    ? '— the files committed to the repo, as built into the image'
                    : run.context_source
                      ? `— source ${run.context_source}`
                      : '— the source was not recorded, so whether an S3 override applied is unknown'}
              </span>
            </>
          )}
        </dd>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <dt className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
          Graph data
        </dt>
        <dd className="min-w-0 text-slate-500">
          {!run.kg_build_date ? (
            <span title="The column arrives with infra/sql/011_kg_build_date.sql and was deliberately not backfilled. A run written before it recorded no vintage; that is a fact about the row, not about the graph.">
              Not recorded — this run predates the graph-vintage stamp
            </span>
          ) : run.kg_build_date === 'unknown' ? (
            <>
              <span className="font-mono text-amber-400">unknown</span>
              <span className="ml-2">
                — no build date was configured and the graph reports none, so this
                run could not say which literature is outside it
              </span>
            </>
          ) : (
            <>
              <span
                className="font-mono text-slate-300"
                title="When the knowledge graph was built. Literature published after it is outside the graph, so a KG null about anything more recent is not evidence of absence. It bounds the graph only — not when the underlying measurements were made."
              >
                built {run.kg_build_date}
              </span>
              <span className="ml-2">
                {run.vintage_source === 'graph'
                  ? '— per the graph’s own build manifest; literature published after it is outside the graph'
                  : run.vintage_source === 'config'
                    ? '— per this deployment’s configuration, not verified against the graph; literature published after it is outside the graph'
                    : '— literature published after it is outside the graph'}
              </span>
            </>
          )}
        </dd>
      </div>
    </dl>
  )
}

/** 21_742_625 → "21.7M". Exact counts are in the API response if anyone needs them. */
function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}
